// src/components/appSettings/Banner/BannerForm/BannerFormSections.jsx
import React from "react";
import { FiUpload, FiX, FiImage } from "react-icons/fi";

// ============= BASIC INFO SECTION =============
export const BannerBasicInfoSection = ({ formData, errors, onChange }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium mb-4">Basic Information</h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Banner Title *
        </label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={onChange}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.title ? "border-red-500" : "border-gray-300"
          }`}
          placeholder="Enter banner title"
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-500">{errors.title}</p>
        )}
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Subtitle
        </label>
        <input
          type="text"
          name="subtitle"
          value={formData.subtitle}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Enter banner subtitle (optional)"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Internal Name
        </label>
        <input
          type="text"
          name="internalName"
          value={formData.internalName}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="For admin reference only"
        />
        <p className="text-xs text-gray-500 mt-1">
          This name is only visible to admins
        </p>
      </div>

      <div>
        <label className="flex items-center mt-8">
          <input
            type="checkbox"
            name="isActive"
            checked={formData.isActive}
            onChange={onChange}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">
            Active (visible to users)
          </span>
        </label>
      </div>
    </div>
  </div>
);

// ============= MEDIA SECTION =============
export const BannerMediaSection = ({
  formData,
  imageFiles,
  imagePreviews,
  errors,
  onImageUpload,
  onImageRemove,
  mode,
}) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium mb-4">Banner Images</h3>
    <p className="text-sm text-gray-500 mb-4">
      Upload banner images for different devices. Desktop and Mobile images are
      required.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Desktop Image */}
      <ImageUploadBox
        label="Desktop Image *"
        type="desktop"
        preview={imagePreviews.desktop}
        onUpload={onImageUpload}
        onRemove={onImageRemove}
        error={errors.desktopImage}
        recommended="Recommended: 1920x600px"
      />

      {/* Mobile Image */}
      <ImageUploadBox
        label="Mobile Image *"
        type="mobile"
        preview={imagePreviews.mobile}
        onUpload={onImageUpload}
        onRemove={onImageRemove}
        error={errors.mobileImage}
        recommended="Recommended: 750x400px"
      />

      {/* Tablet Image */}
      <ImageUploadBox
        label="Tablet Image"
        type="tablet"
        preview={imagePreviews.tablet}
        onUpload={onImageUpload}
        onRemove={onImageRemove}
        error={errors.tabletImage}
        recommended="Recommended: 1024x500px"
      />
    </div>
  </div>
);

// Image Upload Box Component
const ImageUploadBox = ({
  label,
  type,
  preview,
  onUpload,
  onRemove,
  error,
  recommended,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <div
      className={`border-2 border-dashed rounded-lg overflow-hidden ${
        error ? "border-red-500" : "border-gray-300"
      }`}
    >
      {preview ? (
        <div className="relative">
          <img src={preview} alt={label} className="w-full h-40 object-cover" />
          <button
            type="button"
            onClick={() => onRemove(type)}
            className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
          >
            <FiX size={16} />
          </button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center h-40 cursor-pointer hover:bg-gray-50">
          <FiUpload className="text-3xl text-gray-400 mb-2" />
          <span className="text-sm text-gray-500">Click to upload</span>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onUpload(type, e.target.files[0])}
            className="hidden"
          />
        </label>
      )}
    </div>
    {recommended && <p className="text-xs text-gray-500 mt-1">{recommended}</p>}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

// ============= ACTION SECTION =============
export const BannerActionSection = ({ formData, errors, onChange }) => {
  const actionTypes = [
    { value: "NAVIGATION", label: "App Navigation" },
    { value: "DEEPLINK", label: "Deep Link" },
    { value: "WEBVIEW", label: "Web View" },
    { value: "EXTERNAL", label: "External Link" },
    { value: "NONE", label: "No Action" },
  ];

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4">Click Action</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Action Type
          </label>
          <select
            name="action.type"
            value={formData.action.type}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {actionTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {formData.action.type === "NAVIGATION" && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Screen Name *
            </label>
            <input
              type="text"
              name="action.screen"
              value={formData.action.screen}
              onChange={onChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors["action.screen"] ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="e.g., ProductDetails, CategoryList"
            />
            {errors["action.screen"] && (
              <p className="mt-1 text-sm text-red-500">
                {errors["action.screen"]}
              </p>
            )}
          </div>
        )}

        {(formData.action.type === "WEBVIEW" ||
          formData.action.type === "EXTERNAL") && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL *
            </label>
            <input
              type="url"
              name="action.url"
              value={formData.action.url}
              onChange={onChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors["action.url"] ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="https://example.com"
            />
            {errors["action.url"] && (
              <p className="mt-1 text-sm text-red-500">
                {errors["action.url"]}
              </p>
            )}
          </div>
        )}

        {formData.action.type === "DEEPLINK" && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deep Link *
            </label>
            <input
              type="text"
              name="action.deeplink"
              value={formData.action.deeplink}
              onChange={onChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                errors["action.deeplink"] ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="myapp://product/123"
            />
            {errors["action.deeplink"] && (
              <p className="mt-1 text-sm text-red-500">
                {errors["action.deeplink"]}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============= TARGETING SECTION =============
export const BannerTargetingSection = ({
  formData,
  errors,
  branches,
  onChange,
}) => {
  const deviceTypes = [
    { value: "ALL", label: "All Devices" },
    { value: "IOS", label: "iOS Only" },
    { value: "ANDROID", label: "Android Only" },
  ];

  const handleBranchToggle = (branchCode) => {
    const currentBranches = formData.targeting.branches || [];
    const newBranches = currentBranches.includes(branchCode)
      ? currentBranches.filter((b) => b !== branchCode)
      : [...currentBranches, branchCode];

    onChange({
      target: {
        name: "targeting.branches",
        value: newBranches,
      },
    });
  };

  const handleDeviceTypeToggle = (deviceType) => {
    const currentDevices = formData.targeting.deviceTypes || ["ALL"];
    const newDevices = currentDevices.includes(deviceType)
      ? currentDevices.filter((d) => d !== deviceType)
      : [...currentDevices, deviceType];

    onChange({
      target: {
        name: "targeting.deviceTypes",
        value: newDevices,
      },
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4">Targeting</h3>

      <div className="space-y-4">
        {/* Branches */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Target Branches
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Leave empty to show on all branches
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {branches?.map((branch) => (
              <label key={branch.code} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.targeting.branches?.includes(branch.code)}
                  onChange={() => handleBranchToggle(branch.code)}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-sm">
                  {branch.name} ({branch.code})
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Device Types */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Device Types
          </label>
          <div className="flex flex-wrap gap-2">
            {deviceTypes.map((device) => (
              <label key={device.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.targeting.deviceTypes?.includes(
                    device.value
                  )}
                  onChange={() => handleDeviceTypeToggle(device.value)}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="ml-2 text-sm">{device.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* App Version */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Minimum App Version
            </label>
            <input
              type="text"
              name="targeting.minAppVersion"
              value={formData.targeting.minAppVersion}
              onChange={onChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., 1.0.0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Maximum App Version
            </label>
            <input
              type="text"
              name="targeting.maxAppVersion"
              value={formData.targeting.maxAppVersion}
              onChange={onChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., 2.0.0"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

// ============= DISPLAY RULES SECTION =============
export const BannerDisplayRulesSection = ({ formData, errors, onChange }) => {
  const daysOfWeek = [
    { value: 0, label: "Sunday" },
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
  ];

  const handleDayToggle = (day) => {
    const currentDays = formData.displayRules.daysOfWeek || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day];

    onChange({
      target: {
        name: "displayRules.daysOfWeek",
        value: newDays,
      },
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4">Display Rules</h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Priority
          </label>
          <input
            type="number"
            name="displayRules.priority"
            value={formData.displayRules.priority}
            onChange={onChange}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-gray-500 mt-1">
            Higher number = higher priority
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Position
          </label>
          <input
            type="number"
            name="displayRules.position"
            value={formData.displayRules.position}
            onChange={onChange}
            min="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-gray-500 mt-1">Carousel position</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Max Impressions
          </label>
          <input
            type="number"
            name="displayRules.maxImpressions"
            value={formData.displayRules.maxImpressions || ""}
            onChange={onChange}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Unlimited"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            name="displayRules.showOnlyOnce"
            checked={formData.displayRules.showOnlyOnce}
            onChange={onChange}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">
            Show only once per user
          </span>
        </label>
      </div>

      {/* Days of Week */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Show on specific days (leave empty for all days)
        </label>
        <div className="flex flex-wrap gap-2">
          {daysOfWeek.map((day) => (
            <label
              key={day.value}
              className="flex items-center px-3 py-2 border rounded-lg cursor-pointer hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={formData.displayRules.daysOfWeek?.includes(day.value)}
                onChange={() => handleDayToggle(day.value)}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="ml-2 text-sm">{day.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============= SCHEDULING SECTION =============
export const BannerSchedulingSection = ({ formData, errors, onChange }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h3 className="text-lg font-medium mb-4">Schedule</h3>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Valid From *
        </label>
        <input
          type="date"
          name="validFrom"
          value={formData.validFrom}
          onChange={onChange}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.validFrom ? "border-red-500" : "border-gray-300"
          }`}
        />
        {errors.validFrom && (
          <p className="mt-1 text-sm text-red-500">{errors.validFrom}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Valid Until *
        </label>
        <input
          type="date"
          name="validUntil"
          value={formData.validUntil}
          onChange={onChange}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.validUntil ? "border-red-500" : "border-gray-300"
          }`}
        />
        {errors.validUntil && (
          <p className="mt-1 text-sm text-red-500">{errors.validUntil}</p>
        )}
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Campaign
        </label>
        <input
          type="text"
          name="campaign"
          value={formData.campaign}
          onChange={onChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="e.g., Summer Sale 2024"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes (Internal)
        </label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={onChange}
          rows="3"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Internal notes about this banner..."
        />
      </div>
    </div>
  </div>
);
